import DOMPurify from 'dompurify';

// Inbound email HTML is untrusted: it can carry scripts, event handlers, and
// tracking pixels. We render it as markup (the assistant's markdown renderer is
// no help here — email bodies are real HTML), so it MUST be sanitised first.
//
// DOMPurify strips all active content (scripts, event handlers, javascript:
// URLs, <iframe>/<object>/<embed>/<form>, external stylesheets). On top of that
// we:
//   1. Block remote images by default — an opened email must not silently fire a
//      tracking pixel — and report whether any were stripped so the caller can
//      offer a per-message "Bilder anzeigen" toggle (IMM-233 Resolved Decision §6).
//      Blocking covers the WHOLE remote-URL surface, not just `<img src>`:
//      `srcset` (img/source), `<table background>`/`<td background>`, SVG
//      `<image href>` / `xlink:href`, `<input type=image src>`, and inline CSS
//      `url()` / `image-set()` in `style` attributes.
//   2. Force every link to open in a new tab with `rel="noopener noreferrer
//      nofollow"` so a rendered email can neither reach back into the opener nor
//      leak referrer/PageRank.
//
// The remote-URL stripping runs as DOMPurify hooks (registered per call and
// removed afterwards, so no global state leaks between calls) — that inspects
// every element/attribute in one place and is far harder to bypass than an
// ad-hoc `<img>`-only post-pass.

const FORBIDDEN_TAGS = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'link', 'base', 'meta'];

// Attributes that can point at (or list) an image/resource URL and thus trigger
// a remote fetch on render. `srcset` carries a comma-separated candidate list.
const URL_ATTRIBUTES = ['src', 'href', 'xlink:href', 'background', 'poster', 'lowsrc', 'dynsrc'];
const SRCSET_ATTRIBUTES = ['srcset', 'imagesrcset'];

// A URL reaches the network on load unless it is a self-contained `cid:`
// (inline MIME part) or `data:` URI. Everything else — http(s), protocol-
// relative `//host`, or any other absolute scheme — is treated as remote.
function isRemoteUrl(rawUrl: string): boolean {
  const value = rawUrl.trim().toLowerCase();

  if (value === '') return false;

  if (value.startsWith('cid:') || value.startsWith('data:')) return false;

  // Protocol-relative and absolute-scheme URLs are remote. Relative URLs (no
  // scheme, no leading `//`) cannot resolve to a network host in a sandboxed
  // email body and are left alone.
  return (
    value.startsWith('//') ||
    value.startsWith('http:') ||
    value.startsWith('https:') ||
    /^[a-z][a-z0-9+.-]*:/.test(value)
  );
}

// True when a `srcset` candidate list contains any remote URL.
function srcsetHasRemoteUrl(value: string): boolean {
  return value
    .split(',')
    .map((candidate) => candidate.trim().split(/\s+/)[0] ?? '')
    .some((url) => isRemoteUrl(url));
}

// True when an inline `style` value references a remote resource through
// `url(...)` or `image-set(...)` / `-webkit-image-set(...)`.
function styleHasRemoteUrl(style: string): boolean {
  const urlMatches = style.matchAll(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi);

  for (const match of urlMatches) {
    if (isRemoteUrl(match[2] ?? '')) return true;
  }

  return false;
}

// Strip remote `url()`/`image-set()` references from an inline `style` value,
// leaving the rest of the declarations intact.
function stripRemoteUrlsFromStyle(style: string): string {
  return style.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (whole, _quote: string, url: string) =>
    isRemoteUrl(url) ? 'none' : whole,
  );
}

export type SanitiseEmailHtmlResult = {
  // The sanitised HTML, safe to inject via dangerouslySetInnerHTML.
  readonly html: string;
  // True when at least one remote image / resource URL was stripped because
  // remote images were not allowed — drives the "Bilder anzeigen" affordance.
  readonly blockedRemoteImages: boolean;
};

export type SanitiseEmailHtmlOptions = {
  // When false (default) remote images are removed and `blockedRemoteImages` is
  // set if any were present; when true they are kept (member clicked the toggle).
  readonly allowRemoteImages?: boolean;
};

export function sanitiseEmailHtml(dirty: string, options: SanitiseEmailHtmlOptions = {}): SanitiseEmailHtmlResult {
  const allowRemoteImages = options.allowRemoteImages ?? false;

  // Captured by the hooks below; the hook mutates it as it strips remote URLs.
  const state = { blockedRemoteImages: false };

  if (!allowRemoteImages) {
    // One hook covers every element/attribute the parser produced. It removes
    // remote URL-bearing attributes and remote `url()`s inside inline `style`,
    // across the full surface (img/source srcset, table/td background, SVG
    // image href/xlink:href, input[type=image] src, poster, …).
    DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
      const name = data.attrName;
      const value = data.attrValue;

      if (name === 'style') {
        if (styleHasRemoteUrl(value)) {
          state.blockedRemoteImages = true;
          data.attrValue = stripRemoteUrlsFromStyle(value);
        }

        return;
      }

      if (SRCSET_ATTRIBUTES.includes(name)) {
        if (srcsetHasRemoteUrl(value)) {
          state.blockedRemoteImages = true;
          data.keepAttr = false;
        }

        return;
      }

      if (URL_ATTRIBUTES.includes(name) && isRemoteUrl(value)) {
        state.blockedRemoteImages = true;
        data.keepAttr = false;
      }
    });
  }

  let fragment: DocumentFragment;

  try {
    fragment = DOMPurify.sanitize(dirty, {
      FORBID_TAGS: FORBIDDEN_TAGS,
      // Keep the markup as a fragment so we can force link safety precisely.
      RETURN_DOM_FRAGMENT: true,
    });
  } finally {
    // Never let per-call hooks leak into a later call (global DOMPurify state).
    DOMPurify.removeAllHooks();
  }

  // Every surviving anchor opens safely in a new tab. DOMPurify has already
  // dropped javascript:/unsafe hrefs, so a remaining href is a benign protocol.
  for (const anchor of Array.from(fragment.querySelectorAll('a'))) {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer nofollow');
  }

  const container = document.createElement('div');
  container.appendChild(fragment);

  return { html: container.innerHTML, blockedRemoteImages: state.blockedRemoteImages };
}
